import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NgxSpinnerService } from "ngx-spinner";
import { LazyLoadEvent } from "primeng/api";
import { Table } from "primeng/table";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, finalize } from "rxjs/operators";
import { PermissionsService } from "src/app/helpers/permissions.service";
import storageX from "src/app/storageCore/storageX";
import { STORAGE_KEY_ID_PERFIL } from "src/app/storageCore/constStorageKeys";
import * as FileSaver from "file-saver";
import { CockpitService } from "../cockpit/cockpit.service";
import { DriverReportMapComponent } from "../driver-report/driver-report-map.component";

const GRID_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

export const GRID_ENDERECO_PERMISSOES = [
  "ENDERECO_GRID_FUNCIONALIDADE",
  "ENDERECO_GRID",
] as const;

export type GridTerminalRow = {
  idTerminal: number;
  placa?: string;
  frota?: string;
  nomeEmpresa?: string;
  nmMotorista?: string;
  dataOnline?: string;
  dataAtualizacao?: string;
  latitude?: string;
  longitude?: string;
  tag?: string;
  statusLabel: "Online" | "Offline";
  ultimoEvento?: string;
  endereco?: string;
};

@Component({
  selector: "app-grid",
  templateUrl: "./grid.component.html",
  styleUrls: ["./grid.component.scss"],
})
export class GridComponent implements OnInit, OnDestroy {
  @ViewChild("gridTable") gridTable?: Table;

  rows: GridTerminalRow[] = [];
  searchTerm = "";
  statusFilter: "all" | "Online" | "Offline" = "all";
  items: any[] = [];
  permissions: string[] = [];
  exibirEnderecoNoGrid = false;
  tableFirst = 0;
  tableRows = GRID_PAGE_SIZE;
  totalElements = 0;
  loadingGrid = false;
  loadingEnderecos = false;
  gridScrollHeight = "calc(100vh - 300px)";

  private gridLoadSub?: Subscription;
  private enderecoLoadSub?: Subscription;
  private enderecoByTerminalId = new Map<number, string>();
  private searchChange$ = new Subject<string>();
  /** Evita loop do p-table lazy quando a página vem vazia (filtro sem match). */
  private lazyLoadPrimed = false;
  private gridRequestSeq = 0;

  constructor(
    private cockpitService: CockpitService,
    private spinner: NgxSpinnerService,
    public dialog: MatDialog,
    public permissionService: PermissionsService
  ) {}

  ngOnInit(): void {
    this.syncPermissaoEndereco();
    this.loadMenuItems();
    this.searchChange$
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(() => {
        this.tableFirst = 0;
        if (this.gridTable) {
          this.gridTable.first = 0;
        }
        this.loadGrid(true);
      });
  }

  ngOnDestroy(): void {
    this.gridLoadSub?.unsubscribe();
    this.enderecoLoadSub?.unsubscribe();
    this.searchChange$.complete();
  }

  atualizarDados(): void {
    this.tableFirst = 0;
    if (this.gridTable) {
      this.gridTable.first = 0;
    }
    this.enderecoByTerminalId.clear();
    this.loadGrid(true);
  }

  private syncPermissaoEndereco(): void {
    this.permissions = this.permissionService.getPermissions() || [];
    this.exibirEnderecoNoGrid = GRID_ENDERECO_PERMISSOES.some((code) =>
      this.permissionService.hasPermission(code, this.permissions)
    );
  }

  loadGrid(showSpinner = true): void {
    const perfil = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
    if (!perfil) {
      return;
    }

    const page = Math.floor(this.tableFirst / this.tableRows);
    const status =
      this.statusFilter === "all" ? undefined : this.statusFilter;
    const q = this.searchTerm.trim() || undefined;

    const requestSeq = ++this.gridRequestSeq;
    this.loadingGrid = true;
    this.gridLoadSub?.unsubscribe();
    this.enderecoLoadSub?.unsubscribe();

    if (showSpinner) {
      this.spinner.show();
    }

    this.gridLoadSub = this.cockpitService
      .getTerminaisOnlineAndOffline(
        perfil,
        page,
        this.tableRows,
        status,
        q
      )
      .pipe(
        finalize(() => {
          if (requestSeq !== this.gridRequestSeq) {
            return;
          }
          this.loadingGrid = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response) => {
          if (requestSeq !== this.gridRequestSeq) {
            return;
          }
          this.totalElements = response?.totalElements ?? 0;
          if (response?.visualizarEnderecoGrid === true) {
            this.exibirEnderecoNoGrid = true;
          } else if (
            response?.visualizarEnderecoGrid === false &&
            !GRID_ENDERECO_PERMISSOES.some((code) =>
              this.permissionService.hasPermission(code, this.permissions)
            )
          ) {
            this.exibirEnderecoNoGrid = false;
          }
          this.rows = this.mapTerminalRows(response?.content || []);
          this.loadEnderecosPaginaAtual();
        },
        error: () => {
          if (requestSeq !== this.gridRequestSeq) {
            return;
          }
          this.rows = [];
          this.totalElements = 0;
        },
      });
  }

  onLazyLoad(event: LazyLoadEvent): void {
    const nextFirst = event.first ?? 0;
    const nextRows = event.rows ?? this.tableRows;
    const pageChanged =
      nextFirst !== this.tableFirst || nextRows !== this.tableRows;
    this.tableFirst = nextFirst;
    this.tableRows = nextRows;
    const initialLazyLoad = !this.lazyLoadPrimed;
    if (initialLazyLoad || pageChanged) {
      this.lazyLoadPrimed = true;
      this.loadGrid(initialLazyLoad || pageChanged);
    }
  }

  private loadEnderecosPaginaAtual(): void {
    if (!this.exibirEnderecoNoGrid) {
      return;
    }

    const perfil = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
    if (!perfil) {
      return;
    }

    const pendentes = this.rows.filter(
      (row) =>
        row.latitude &&
        row.longitude &&
        !this.enderecoByTerminalId.has(row.idTerminal)
    );

    if (pendentes.length === 0) {
      this.patchEnderecosNasLinhas();
      return;
    }

    const lote = pendentes.slice(0, GRID_PAGE_SIZE).map((row) => ({
      idTerminal: row.idTerminal,
      latitude: row.latitude,
      longitude: row.longitude,
    }));

    this.loadingEnderecos = true;
    this.enderecoLoadSub?.unsubscribe();

    this.enderecoLoadSub = this.cockpitService
      .getGridEnderecos(perfil, lote)
      .pipe(finalize(() => (this.loadingEnderecos = false)))
      .subscribe({
        next: (itens) => {
          (itens || []).forEach((item) => {
            if (item?.idTerminal != null && item.endereco) {
              this.enderecoByTerminalId.set(item.idTerminal, item.endereco);
            }
          });
          this.patchEnderecosNasLinhas();
        },
        error: () => {},
      });
  }

  private patchEnderecosNasLinhas(): void {
    this.rows = this.rows.map((row) => {
      const endereco = this.enderecoByTerminalId.get(row.idTerminal);
      return endereco ? { ...row, endereco } : { ...row, endereco: undefined };
    });
  }

  private mapTerminalRows(items: any[]): GridTerminalRow[] {
    return (items || []).map((element) => ({
      idTerminal: element.idTerminal,
      placa: element.placa,
      frota: element.frota,
      nomeEmpresa: element.nomeEmpresa,
      nmMotorista: element.nmMotorista,
      dataOnline: element.dataOnline,
      dataAtualizacao: element.dataAtualizacao,
      latitude: element.latitude,
      longitude: element.longitude,
      tag: element.tag,
      statusLabel: element.online ? "Online" : "Offline",
      ultimoEvento: element.ultimoEvento,
    }));
  }

  formatDisplayDate(value?: string): string {
    if (value == null || value === "") {
      return "-";
    }

    return String(value).replace(/^'|'$/g, "");
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChange$.next(value);
  }

  onStatusFilterChange(value: "all" | "Online" | "Offline"): void {
    this.statusFilter = value;
    this.tableFirst = 0;
    if (this.gridTable) {
      this.gridTable.first = 0;
    }
    this.loadGrid(true);
  }

  openMap(row: GridTerminalRow): void {
    const enderecoGrid =
      this.exibirEnderecoNoGrid && row.endereco
        ? row.endereco.trim()
        : undefined;

    this.dialog.open(DriverReportMapComponent, {
      width: "520px",
      maxHeight: "90vh",
      data: {
        lat: row.latitude,
        lng: row.longitude,
        placa: row.placa,
        endereco: enderecoGrid,
        autoFetchAddress: !enderecoGrid,
      },
    });
  }

  private loadMenuItems(): void {
    this.items = [
      {
        label: "Exportar",
        items: [
          {
            label: "Excel",
            icon: "pi pi-file-excel",
            command: () => this.exportExcel(),
          },
          {
            label: "PDF",
            icon: "pi pi-file-pdf",
            command: () => this.exportPdf(),
          },
        ],
      },
    ];
  }

  exportPdf(): void {
    if (this.rows.length === 0) {
      return;
    }

    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default(0, 0);
        doc.autoTable({
          startY: 10,
          html: ".grid-table",
          useCss: true,
        });
        doc.save("grid_terminais.pdf");
      });
    });
  }

  exportExcel(): void {
    if (this.rows.length === 0) {
      return;
    }

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.table_to_sheet(
        document.getElementsByClassName("grid-table")[0]
      );
      const workbook = {
        Sheets: { data: worksheet },
        SheetNames: ["data"],
      };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      this.saveAsExcelFile(excelBuffer, "grid_terminais");
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
  }
}
